import axios from "axios";

const liveBaseURL = process.env.REACT_APP_BACKEND_APP_URL;
const apiURL = liveBaseURL;
const shareUrl = process.env.REACT_APP_SHARE_URL;
const websocketURL = process.env.REACT_APP_WEBSOCKET_APP_URL;
const baseURL = `${apiURL}/`;
const wsURL = `${websocketURL}/`;

/**
 * Generates a UUID v4.
 * Uses native crypto.randomUUID() — falls back to manual RFC 4122 if unavailable.
 */
const generateUUID = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID(); // native — e.g. "110e8400-e29b-41d4-a716-446655440000"
  }

  // Fallback for older browsers / non-secure contexts
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
};

/**
 * Detects the type of request data and injects x_idempotency_key accordingly.
 * Handles: FormData | URLSearchParams | JSON string | plain object | empty body
 */
const injectIntoBody = (data, key) => {
  // ── FormData (multipart/form-data file uploads) ──────────────────────────
  if (data instanceof FormData) {
    if (!data.has("x_idempotency_key")) {
      data.append("x_idempotency_key", key);
    }
    return data;
  }

  // ── URLSearchParams (application/x-www-form-urlencoded) ──────────────────
  if (data instanceof URLSearchParams) {
    if (!data.has("x_idempotency_key")) {
      data.append("x_idempotency_key", key);
    }
    return data;
  }

  // ── JSON string (axios may pre-serialize the body) ────────────────────────
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      return JSON.stringify({ ...parsed, x_idempotency_key: key });
    } catch {
      console.warn("[axios] Could not inject idempotency key: body is a non-JSON string");
      return data;
    }
  }

  // ── Plain object / undefined / null ───────────────────────────────────────
  return {
    ...(data ?? {}),
    x_idempotency_key: key,
  };
};

const instance = axios.create({
  baseURL,
  timeout: 30000,
  retry: 3,
  retryDelay: 1000,
});

instance.interceptors.request.use(
  (config) => {
    if (config.retry === undefined) {
      config.retry = instance.defaults.retry;
    }

    // Generate UUID once, reuse same key on retries
    const idempotencyKey = config._idempotencyKey || generateUUID();
    config._idempotencyKey = idempotencyKey;

    const method = config.method?.toLowerCase();

    if (method === "get" || method === "delete") {
      config.params = {
        ...config.params,
        x_idempotency_key: idempotencyKey,
      };
    } else {
      config.data = injectIntoBody(config.data, idempotencyKey);
    }

    return config;
  },
  (error) => Promise.reject(error)
);

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, message } = error;

    if (config && config.retry > 0) {
      const shouldRetry =
        !error.response &&
        (message.includes("timeout") ||
          message.includes("Network Error") ||
          message.includes("ERR_NETWORK") ||
          message.includes("ERR_INTERNET_DISCONNECTED"));

      if (shouldRetry) {
        config.retry -= 1;

        const delay =
          config.retryDelay * Math.pow(2, instance.defaults.retry - config.retry);

        console.log(
          `Retrying (${instance.defaults.retry - config.retry + 1}/${instance.defaults.retry}) after ${delay}ms — Key: ${config._idempotencyKey}`
        );

        await new Promise((resolve) => setTimeout(resolve, delay));
        return instance(config);
      }
    }

    return Promise.reject(error);
  }
);

export { baseURL, wsURL, liveBaseURL, shareUrl };
export default instance;